/**
 * AB#1797 (CR-2026-05-29 + CR-2026-05-30 + CR-2026-05-30-v2) W1 — Appointments List V2 page.
 *
 * Route: /appointments
 * Feature flag: REACT_APP_APPOINTMENTS_LIST_V2_ENABLED (default OFF)
 *
 * CR-2026-05-30-v2: refactored filter row to Dina's mockup pattern.
 *   - STATUS chip group with colored dots (All | Booked | Confirmed | Arrived | Started | Completed | No-show)
 *     (Confirmed added AB#2164)
 *   - PAYMENT chip group already existed (All | Unpaid | Paid | Refunded) — chips get dots
 *   - Branch CSV input → multi-select dropdown (shows "2 of 3" when partial)
 *   - Staff CSV input → single-select dropdown (shows "Anyone" or staff name)
 *   - "Clear filters" link on the right of the filter row
 *
 * RTL: all CSS via logical properties.  String keys via t() — zero hardcoded text.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useSalonOwnerAppointments } from '../../../hooks/useSalonOwnerAppointments';
import { useAppointmentsRealtime } from '../../../hooks/useAppointmentsRealtime';
import { MarkPaidPaymentMethod, SalonOwnerAppointmentRowDto } from '../../../services/appointmentsList';
import { apiService } from '../../../services/api';
import { generateIdempotencyKey, listBookingModel, staffBookingModel, StaffBookingFlow } from '../../../components/BookingDetail/staffBooking';
import PaymentBadge from '../../../components/PaymentBadge/PaymentBadge';
import MarkAsPaidModal from '../../../components/MarkAsPaidModal/MarkAsPaidModal';
import UpgradeChip from '../../../components/UpgradeChip/UpgradeChip';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import DateField from '../../../components/DateField/DateField';
import { trackEvent } from '../../../lib/analytics';
import BookingDetailDialog, { formatBookingDate } from '../../../components/BookingDetail/BookingDetailDialog';
import BookingActions from '../../../components/BookingDetail/BookingActions';
import BookingStatusPill from '../../../components/BookingDetail/BookingStatusPill';
import { bookingStatusColors, bookingStatusLabelKey, bookingStatusTone, toLifecycleStatus, type LifecycleStatus } from '../../../lib/bookingLifecycle';
import s from './AppointmentsListPage.module.css';

const useTranslation = useTranslationUntyped as any;

// Feature flag
const FLAG_ENABLED = process.env.REACT_APP_APPOINTMENTS_LIST_V2_ENABLED === 'true';

// ─── Tier detection from JWT (plan tier = Starter / Growth / Pro / Enterprise)
//
// The BE (Devanza.API/Services/CurrentUserService.cs:55-56) stamps the claim
// `planTier` as the ENUM NAME (string), e.g. "Starter" / "Growth" / "Pro" /
// "Enterprise" — NOT a numeric. Parse the string directly; also tolerate a
// numeric value in case the BE changes its serialization later.
//
// UAT OVERRIDE (CR-2026-05-30): the final return is hardcoded to 'Pro' so
// the founder can exercise the full Growth+ filter set against Hilltop
// dogfood (which is a Starter plan in dev today). Revert this override
// when the temp force-on commit is reverted — restore the `tier` return.
function usePlanTier(): 'Starter' | 'Growth' | 'Pro' | 'Enterprise' {
    const { accessToken } = useAuthContext();
    let tier: 'Starter' | 'Growth' | 'Pro' | 'Enterprise' = 'Starter';
    if (accessToken) {
        try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const raw = payload.planTier;
            if (typeof raw === 'string') {
                if (raw === 'Enterprise') tier = 'Enterprise';
                else if (raw === 'Pro') tier = 'Pro';
                else if (raw === 'Growth') tier = 'Growth';
                else if (raw === 'Starter') tier = 'Starter';
            } else if (typeof raw === 'number') {
                if (raw >= 4) tier = 'Enterprise';
                else if (raw >= 3) tier = 'Pro';
                else if (raw >= 2) tier = 'Growth';
                else tier = 'Starter';
            }
        } catch { /* */ }
    }
    // UAT OVERRIDE — replace with `return tier;` when reverting force-on.
    void tier;
    return 'Pro';
}

// ─── User profile ID from JWT (for toast suppression on own actions)
function useUserProfileId(): number | undefined {
    const { accessToken } = useAuthContext();
    if (!accessToken) return undefined;
    try {
        const p = JSON.parse(atob(accessToken.split('.')[1]));
        return typeof p.userProfileId === 'number' ? p.userProfileId : undefined;
    } catch { return undefined; }
}

// ─── User role
// AB#2175 (Batch A) — the JWT carries `userType`, not a `role` claim, so the old
// `p.role === 'salonstaff'` parse never matched: staff got the owner view (an empty
// list, since the BE answers staff with itemsForStaff, plus owner-only dropdown calls).
// AuthContext already derives the role from `userType`.
function useIsStaffRole(): boolean {
    return useAuthContext().isSalonStaff;
}

// O1 (AB#2199) / S1 (AB#2204) — list row → shared booking detail model and the
// staff booking detail live in components/BookingDetail/staffBooking (shared with
// My Day and the notification bell, S2 AB#2205).
export { staffBookingModel };

// ─── Status filter chips matching Dina mockup §6 palette
//   Dina specifies 5 status chips after "All". "Booked" represents the
//   pre-arrival group (Pending OR Approved). The chip text wins over the
//   raw BE enum, so we map back when filtering.
//   AB#2164 — "Confirmed" narrows to owner-confirmed bookings only (wire
//   status `Approved`, product label "Confirmed").
//   O3 (SO-10/SO-42) — chips use the owner lifecycle words + colours
//   (lib/bookingLifecycle): "Awaiting confirmation" replaces the old "Booked"
//   group, and Cancelled gets its own chip.
type StatusChipKey = 'all' | 'pending' | 'confirmed' | 'arrived' | 'started' | 'completed' | 'noshow' | 'cancelled';
const STATUS_CHIPS: { key: StatusChipKey; csv: string; status: LifecycleStatus | null }[] = [
    { key: 'all', csv: '', status: null },
    { key: 'pending', csv: 'Pending', status: 'Pending' },
    { key: 'confirmed', csv: 'Approved', status: 'Approved' },
    { key: 'arrived', csv: 'Arrived', status: 'Arrived' },
    { key: 'started', csv: 'Started', status: 'Started' },
    { key: 'completed', csv: 'Completed', status: 'Completed' },
    { key: 'noshow', csv: 'NoShow', status: 'NoShow' },
    { key: 'cancelled', csv: 'Cancelled', status: 'Cancelled' },
];

type PaymentChipKey = 'all' | 'unpaid' | 'paid' | 'refunded';
const PAYMENT_CHIPS: { key: PaymentChipKey; value: string; cssMod: string }[] = [
    { key: 'all', value: '', cssMod: 'all' },
    { key: 'unpaid', value: 'Unpaid', cssMod: 'unpaid' },
    { key: 'paid', value: 'Paid', cssMod: 'paid' },
    { key: 'refunded', value: 'Refunded', cssMod: 'refunded' },
];

// ─── Detect the currently-active status chip from the BE status CSV.
//   Used to highlight a chip when the page is loaded via Pending tab or any
//   future URL-driven default.
function detectStatusChip(csv: string): StatusChipKey {
    if (!csv) return 'all';
    // Match by exact CSV first
    const exact = STATUS_CHIPS.find(c => c.csv === csv);
    if (exact) return exact.key;
    // O3 — no exact match (e.g. the Upcoming tab's multi-status filter) → no
    // single status chip is highlighted; highlighting a subset was misleading.
    return 'all';
}

interface BranchOption { id: number; name: string; }
interface StaffOption { id: number; name: string; }

const AppointmentsListPage: React.FC = () => {
    const { t } = useTranslation('appointments');
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t: tl, language } = useLanguage();
    const tier = usePlanTier();
    const isStaff = useIsStaffRole();
    const userProfileId = useUserProfileId();

    // CR-2026-05-30: Top-level Tabs (Today / Upcoming / Past / Pending / All).
    // Each tab presets paymentFilter + statusFilter + dateFrom + dateTo on
    // change, so the filter chip row reflects the active tab's scope.
    type TabKey = 'today' | 'upcoming' | 'past' | 'pending' | 'all';
    const [tabKey, setTabKey] = useState<TabKey>('today');

    // Filter state. O1 (AB#2199, SO-08) — the payment filter starts at All: the old
    // "Unpaid" default silently hid paid bookings, so the list never matched the Calendar.
    const [paymentFilter, setPaymentFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    // CR-2026-05-30-v2: real multi-select / single-select instead of CSV inputs.
    const [branchSelected, setBranchSelected] = useState<number[]>([]);
    const [staffSelected, setStaffSelected] = useState<number | null>(null);
    // CR-2026-05-30: free-text search across customer name + phone.
    const [searchInput, setSearchInput] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    // CR-2026-05-30: page-size selector (BE clamps 10..100, default 50).
    const [pageSize, setPageSize] = useState<number>(50);

    // CR-2026-05-30-v2: populate Branch + Staff dropdowns on mount.
    const [branches, setBranches] = useState<BranchOption[]>([]);
    const [staffList, setStaffList] = useState<StaffOption[]>([]);
    const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);
    const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
    const branchDropdownRef = useRef<HTMLDivElement | null>(null);
    const staffDropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Only owners need to populate Branch/Staff dropdowns; staff are auto-scoped.
        if (isStaff) return;
        let active = true;
        (async () => {
            try {
                const res = await apiService.getBranches();
                if (!active) return;
                const active2: BranchOption[] = (res?.branches ?? [])
                    .filter((b: any) => b.active !== false)
                    .map((b: any) => ({ id: b.id, name: b.name }));
                setBranches(active2);
            } catch { /* silent — empty dropdown */ }
            try {
                const res = await apiService.getBusinessStaff({ page: 1, pageSize: 200 });
                if (!active) return;
                const mapped: StaffOption[] = (res?.staff ?? []).map((sf: any) => ({
                    id: sf.id,
                    name: sf.fullName ?? sf.displayName ?? `Staff ${sf.id}`,
                }));
                setStaffList(mapped);
            } catch { /* silent */ }
        })();
        return () => { active = false; };
    }, [isStaff]);

    // Close dropdowns on outside-click.
    useEffect(() => {
        const onClick = (ev: MouseEvent) => {
            const target = ev.target as Node;
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(target)) {
                setBranchDropdownOpen(false);
            }
            if (staffDropdownRef.current && !staffDropdownRef.current.contains(target)) {
                setStaffDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // Debounce the search input → search query (300ms).
    useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => window.clearTimeout(id);
    }, [searchInput]);

    // Tab-driven preset. Switching tabs OVERWRITES date+status; Payment chip
    // and custom Date Range / Branch / Staff / Search remain user-controlled.
    const applyTabPreset = useCallback((next: TabKey) => {
        setTabKey(next);
        const today = new Date();
        const yyyymmdd = (d: Date) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        const todayISO = yyyymmdd(today);
        const week = new Date(today); week.setUTCDate(week.getUTCDate() + 7);
        const weekISO = yyyymmdd(week);
        switch (next) {
            case 'today':
                setDateFrom(todayISO); setDateTo(todayISO); setStatusFilter(''); break;
            case 'upcoming': {
                const tomorrow = new Date(today); tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
                setDateFrom(yyyymmdd(tomorrow)); setDateTo(weekISO); setStatusFilter('Pending,Approved,Arrived,Started'); break;
            }
            case 'past':
                setDateFrom(''); setDateTo(todayISO); setStatusFilter('Completed,NoShow,Cancelled'); break;
            case 'pending':
                setDateFrom(''); setDateTo(''); setStatusFilter('Pending'); break;
            case 'all':
                setDateFrom(''); setDateTo(''); setStatusFilter(''); break;
        }
        try { trackEvent('appointments.filter.applied', { filterType: 'tab', filterValue: next }); } catch { /* */ }
    }, []);

    // S2 (AB#2205) — deep links from My Day and the notification bell:
    // ?tab=pending (awaiting confirmation), ?payment=unpaid, ?open=<bookingId>.
    const [searchParams, setSearchParams] = useSearchParams();

    // Run the initial preset once on mount (Today, or the linked tab).
    useEffect(() => {
        const linkedTab = searchParams.get('tab');
        const known: TabKey[] = ['today', 'upcoming', 'past', 'pending', 'all'];
        applyTabPreset(known.includes(linkedTab as TabKey) ? linkedTab as TabKey : 'today');
        if (searchParams.get('payment') === 'unpaid') setPaymentFilter('Unpaid');
        // AB#2206 — the Dashboard's "Completed, not paid" tile links ?status=Completed so
        // the list shows exactly the bookings it counted (one known status only).
        const linkedStatus = searchParams.get('status');
        if (linkedStatus && STATUS_CHIPS.some(c => c.csv === linkedStatus && c.csv !== '')) setStatusFilter(linkedStatus);
        // AB#2206 — …and ?branchId=N when the Dashboard is scoped to one branch.
        const linkedBranch = Number(searchParams.get('branchId'));
        if (Number.isInteger(linkedBranch) && linkedBranch > 0) setBranchSelected([linkedBranch]);
        // Applied once — leave the URL clean (like ?open) so a refresh starts fresh.
        if (searchParams.has('tab') || searchParams.has('payment') || searchParams.has('status') || searchParams.has('branchId')) {
            const next = new URLSearchParams(searchParams);
            next.delete('tab');
            next.delete('payment');
            next.delete('status');
            next.delete('branchId');
            setSearchParams(next, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mark-paid modal state
    const [modalAppointment, setModalAppointment] = useState<SalonOwnerAppointmentRowDto | null>(null);
    const idempotencyKeyRef = useRef<string>('');

    const isGrowthPlus = tier === 'Growth' || tier === 'Pro' || tier === 'Enterprise';

    // CSV helpers for the BE call.
    const branchCsv = useMemo(() => branchSelected.join(','), [branchSelected]);
    const staffCsv = useMemo(() => (staffSelected != null ? String(staffSelected) : ''), [staffSelected]);

    const params = {
        paymentStatus: paymentFilter || undefined,
        status: statusFilter || undefined,
        from: (isGrowthPlus && dateFrom) ? dateFrom : undefined,
        to: (isGrowthPlus && dateTo) ? dateTo : undefined,
        branchIds: (isGrowthPlus && !isStaff && branchCsv) ? branchCsv : undefined,
        staffIds: (isGrowthPlus && !isStaff && staffCsv) ? staffCsv : undefined,
        search: search || undefined,
        pageSize,
    };

    const { data, isLoading, isError, refetch } = useSalonOwnerAppointments(params);
    useAppointmentsRealtime(userProfileId);

    // D1: list.viewed — once per session
    const viewedRef = useRef(false);
    useEffect(() => {
        if (!FLAG_ENABLED || viewedRef.current) return;
        viewedRef.current = true;
        try {
            trackEvent('appointments.list.viewed', {
                role: isStaff ? 'staff' : 'owner',
            });
        } catch { /* */ }
    }, [isStaff]);

    const handleFilterChange = useCallback((filterType: string, value: string) => {
        switch (filterType) {
            case 'paymentStatus': setPaymentFilter(value); break;
            case 'status': setStatusFilter(value); break;
            default: break;
        }
        try {
            trackEvent('appointments.filter.applied', { filterType, filterValue: value });
        } catch { /* */ }
    }, []);

    const handleStatusChipClick = (chip: typeof STATUS_CHIPS[number]) => {
        handleFilterChange('status', chip.csv);
    };

    const handleClearFilters = () => {
        setPaymentFilter('');
        setStatusFilter('');
        setBranchSelected([]);
        setStaffSelected(null);
        setSearchInput('');
        // Keep tabs's date preset; "Today" tab's dates remain.
        applyTabPreset(tabKey);
        try { trackEvent('appointments.filter.applied', { filterType: 'clear', filterValue: 'all' }); } catch { /* */ }
    };

    const handleOpenMarkPaid = (appointment: SalonOwnerAppointmentRowDto) => {
        idempotencyKeyRef.current = generateIdempotencyKey();
        setModalAppointment(appointment);
    };

    const handleMarkPaidSuccess = (_method: MarkPaidPaymentMethod) => {
        showToast(t('markPaidModal.success'), 'success');
        setModalAppointment(null);
    };

    // O1 (AB#2199, SO-06) — the booking open in the shared detail dialog.
    const [detailId, setDetailId] = useState<number | null>(null);

    // S2 (AB#2205) — ?open=<id> (a staff notification / My Day link) opens that
    // booking's detail, then leaves the URL clean so a refresh doesn't reopen it.
    const openParam = searchParams.get('open');
    useEffect(() => {
        if (!openParam) return;
        const id = Number(openParam);
        if (isStaff && Number.isInteger(id) && id > 0) setDetailId(id);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openParam, isStaff]);

    const openInCalendar = (appointment: SalonOwnerAppointmentRowDto) => {
        // CR-2026-05-30-v3 — Carry `branchId` through the URL so the Calendar
        // page can auto-swap its branch picker to the appointment's branch.
        // Without this, the Calendar opens on whatever branch was last viewed
        // (localStorage), and an appointment booked at a different branch
        // appears invisible to the founder ("I see it in the list but not in
        // Calendar"). The Calendar reads ?branchId= on mount, sets state, and
        // strips the param so the picker behaves normally afterwards.
        const date = appointment.appointmentDate.slice(0, 10);
        navigate(`/calendar?day=${date}&branchId=${appointment.branchId}&apptId=${appointment.id}&from=list`);
    };

    const handleRowClick = (appointment: SalonOwnerAppointmentRowDto) => {
        // O1 (AB#2199, SO-06) — open the booking right here (same detail + actions as the
        // Calendar); "Open in calendar" inside it lands on the booking's own branch + day.
        // S1 (AB#2204, SG-05) — staff open the same detail for their own bookings
        // (staff-scoped, no Calendar link: the Calendar is an owner-only route).
        setDetailId(appointment.id);
    };

    // Ownership items (Role-based): owner gets itemsForOwner, staff gets itemsForStaff
    const items = isStaff
        ? (data?.itemsForStaff ?? [])
        : (data?.itemsForOwner ?? []);

    const counts = data?.counts;
    const totalCount = (counts?.paid ?? 0) + (counts?.unpaid ?? 0) + (counts?.refunded ?? 0);
    const activeStatusChip = detectStatusChip(statusFilter);

    // Branch dropdown label: "All", or "Name", or "N of M"
    const branchLabel = (() => {
        if (branchSelected.length === 0) return t('list.filter.branchAll');
        if (branchSelected.length === branches.length && branches.length > 0) return t('list.filter.branchAll');
        if (branchSelected.length === 1) {
            const b = branches.find(b => b.id === branchSelected[0]);
            return b?.name ?? `#${branchSelected[0]}`;
        }
        return t('list.filter.branchCountOfTotal', { count: branchSelected.length, total: branches.length });
    })();

    const staffLabel = (() => {
        if (staffSelected == null) return t('list.filter.staffAnyone');
        const sf = staffList.find(s2 => s2.id === staffSelected);
        return sf?.name ?? `#${staffSelected}`;
    })();

    const hasActiveFilter = !!(paymentFilter || statusFilter || branchSelected.length > 0 || staffSelected != null || search);

    return (
        <div className={s.page}>
            <div className={s.toolbar}>
                <h1 className={s.pageTitle}>{t('list.pageTitle')}</h1>
                {isStaff && (
                    <span className={s.scopePill}>{t('list.myAppointments')}</span>
                )}
                {!isStaff && (
                    <span className={s.scopePill}>{t('list.allBranches')}</span>
                )}
            </div>

            {/* CR-2026-05-30 — Top-level Tab row. Each tab presets date + status.
                Payment filter + Search + Branch/Staff/PageSize remain user-controlled. */}
            <div className={s.tabRow} role="tablist" aria-label={tl('appt.tabsAria')}>
                {(['today', 'upcoming', 'past', 'pending', 'all'] as const).map(k => (
                    <button
                        key={k}
                        type="button"
                        role="tab"
                        aria-selected={tabKey === k}
                        className={`${s.tab} ${tabKey === k ? s.tabActive : ''}`}
                        onClick={() => applyTabPreset(k)}
                    >
                        {t(`list.tabs.${k}`)}
                    </button>
                ))}
            </div>

            {/* CR-2026-05-30-v3 — Status + Payment chip groups. Two-section
                bar with a vertical divider between groups. Active chip is
                tinted with its dot color (Unpaid active → amber, Paid active
                → green) instead of a generic accent fill — so the user sees
                at a glance which filter is on, in BOTH groups, without
                confusing the two "All" chips. Per-payment counts shown
                where the BE provides them; zero counts dim the chip. */}
            <div className={s.chipBar} role="toolbar" aria-label={t('list.filter.status') + ' / ' + t('list.filter.payment')}>
                {/* STATUS group */}
                <div className={s.chipSection}>
                    <span className={s.chipSectionLabel} id="appt-filter-status-label">{t('list.filter.status')}</span>
                    <div className={s.chipGroup} role="group" aria-label={t('list.filter.status')}>
                        {STATUS_CHIPS.map(chip => {
                            const isActive = activeStatusChip === chip.key;
                            const count = chip.key === 'all' ? totalCount : null;
                            return (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={`${s.chip} ${chip.status ? s[`chip--tone-${bookingStatusTone(chip.status)}`] ?? '' : ''} ${isActive ? s.chipActive : ''}`}
                                    aria-pressed={isActive}
                                    // O3 (SO-44) — named "Status, <chip>" (group label + the chip's own text).
                                    id={`appt-status-chip-${chip.key}`}
                                    aria-labelledby={`appt-filter-status-label appt-status-chip-${chip.key}`}
                                    onClick={() => handleStatusChipClick(chip)}
                                >
                                    {chip.status && <span className={s.chipDot} style={{ background: bookingStatusColors(chip.status).fg }} aria-hidden="true" />}
                                    <span className={s.chipLabel}>{chip.status ? tl(bookingStatusLabelKey(chip.status)) : t('list.statusChip.all')}</span>
                                    {count != null && count > 0 && (
                                        <span className={s.chipCount}>{count}</span>
                                    )}
                                </button>
                            );
                        })}
                </div>
                </div>

                <span className={s.chipBarDivider} aria-hidden="true" />

                {/* PAYMENT group */}
                <div className={s.chipSection}>
                    <span className={s.chipSectionLabel} id="appt-filter-payment-label">{t('list.filter.payment')}</span>
                    <div className={s.chipGroup} role="group" aria-label={t('list.filter.payment')}>
                        {PAYMENT_CHIPS.map(chip => {
                            const isActive = paymentFilter === chip.value;
                            // Per-chip count from BE response. Show on every
                            // payment chip when there's a value to show; dim
                            // zero-count chips so empty filters don't shout.
                            const count: number | null =
                                chip.key === 'unpaid' ? counts?.unpaid ?? null :
                                chip.key === 'paid' ? counts?.paid ?? null :
                                chip.key === 'refunded' ? counts?.refunded ?? null :
                                chip.key === 'all' ? totalCount :
                                null;
                            const isZero = count === 0;
                            return (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={`${s.chip} ${s[`chip--${chip.cssMod}`] || ''} ${isActive ? s.chipActive : ''} ${isZero && !isActive ? s.chipZero : ''}`}
                                    aria-pressed={isActive}
                                    // O3 (SO-44) — named "Payment, <chip>".
                                    id={`appt-payment-chip-${chip.key}`}
                                    aria-labelledby={`appt-filter-payment-label appt-payment-chip-${chip.key}`}
                                    onClick={() => handleFilterChange('paymentStatus', chip.value)}
                                >
                                    {chip.key !== 'all' && <span className={`${s.chipDot} ${s[`chipDot--${chip.cssMod}`] || ''}`} aria-hidden="true" />}
                                    <span className={s.chipLabel}>{chip.key === 'all' ? t('all') : t(`list.payment.${chip.key}`)}</span>
                                    {count != null && count > 0 && (
                                        <span className={s.chipCount}>{count}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* CR-2026-05-30-v2 — Branch + Staff + Date range + Clear filters row */}
            <div className={s.filterRow}>
                {/* Branch dropdown — Growth+ Owner only */}
                {!isStaff && (
                    <div className={`${s.dropdownWrap} ${!isGrowthPlus ? s.filterLocked : ''}`} ref={branchDropdownRef}>
                        {!isGrowthPlus && (
                            <UpgradeChip tier="Growth" featureKey="branch-filter" position="inline" />
                        )}
                        {isGrowthPlus && (
                            <>
                                <button
                                    type="button"
                                    className={s.dropdownToggle}
                                    aria-haspopup="listbox"
                                    aria-expanded={branchDropdownOpen}
                                    onClick={() => setBranchDropdownOpen(o => !o)}
                                >
                                    <span className={s.dropdownLabel}>{t('list.filter.branch')}</span>
                                    <span className={s.dropdownValue}>{branchLabel}</span>
                                    <span className={s.dropdownCaret} aria-hidden="true">▾</span>
                                </button>
                                {branchDropdownOpen && (
                                    <div className={s.dropdownMenu} role="listbox" aria-multiselectable="true">
                                        <button
                                            type="button"
                                            className={s.dropdownItem}
                                            onClick={() => { setBranchSelected([]); setBranchDropdownOpen(false); }}
                                        >
                                            <span className={s.dropdownCheckbox} aria-hidden="true">{branchSelected.length === 0 ? '◉' : '○'}</span>
                                            {t('list.filter.branchAll')}
                                        </button>
                                        {branches.map(b => {
                                            const checked = branchSelected.includes(b.id);
                                            return (
                                                <button
                                                    key={b.id}
                                                    type="button"
                                                    className={s.dropdownItem}
                                                    role="option"
                                                    aria-selected={checked}
                                                    onClick={() => {
                                                        setBranchSelected(prev => checked ? prev.filter(x => x !== b.id) : [...prev, b.id]);
                                                    }}
                                                >
                                                    <span className={s.dropdownCheckbox} aria-hidden="true">{checked ? '☑' : '☐'}</span>
                                                    {b.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Staff dropdown — Growth+ Owner only */}
                {!isStaff && (
                    <div className={`${s.dropdownWrap} ${!isGrowthPlus ? s.filterLocked : ''}`} ref={staffDropdownRef}>
                        {!isGrowthPlus && (
                            <UpgradeChip tier="Growth" featureKey="staff-filter" position="inline" />
                        )}
                        {isGrowthPlus && (
                            <>
                                <button
                                    type="button"
                                    className={s.dropdownToggle}
                                    aria-haspopup="listbox"
                                    aria-expanded={staffDropdownOpen}
                                    onClick={() => setStaffDropdownOpen(o => !o)}
                                >
                                    <span className={s.dropdownLabel}>{t('list.filter.staff')}</span>
                                    <span className={s.dropdownValue}>{staffLabel}</span>
                                    <span className={s.dropdownCaret} aria-hidden="true">▾</span>
                                </button>
                                {staffDropdownOpen && (
                                    <div className={s.dropdownMenu} role="listbox">
                                        <button
                                            type="button"
                                            className={s.dropdownItem}
                                            onClick={() => { setStaffSelected(null); setStaffDropdownOpen(false); }}
                                        >
                                            <span className={s.dropdownCheckbox} aria-hidden="true">{staffSelected == null ? '◉' : '○'}</span>
                                            {t('list.filter.staffAnyone')}
                                        </button>
                                        {staffList.map(sf => {
                                            const checked = staffSelected === sf.id;
                                            return (
                                                <button
                                                    key={sf.id}
                                                    type="button"
                                                    className={s.dropdownItem}
                                                    role="option"
                                                    aria-selected={checked}
                                                    onClick={() => { setStaffSelected(sf.id); setStaffDropdownOpen(false); }}
                                                >
                                                    <span className={s.dropdownCheckbox} aria-hidden="true">{checked ? '◉' : '○'}</span>
                                                    {sf.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Date range — Growth+ */}
                <div className={`${s.dateWrap} ${!isGrowthPlus ? s.filterLocked : ''}`}>
                    <span className={s.dropdownLabel}>{t('list.filter.dateRange')}</span>
                    {!isGrowthPlus && (
                        <UpgradeChip tier="Growth" featureKey="date-range" position="inline" />
                    )}
                    {isGrowthPlus && (
                        <>
                            <DateField
                                label={t('list.filter.from')}
                                className={s.dateInput}
                                value={dateFrom}
                                max={dateTo || undefined}
                                onChange={iso => { setDateFrom(iso); handleFilterChange('dateFrom', iso); }}
                            />
                            <span className={s.dateSeparator} aria-hidden="true">→</span>
                            <DateField
                                label={t('list.filter.to')}
                                className={s.dateInput}
                                value={dateTo}
                                min={dateFrom || undefined}
                                onChange={iso => { setDateTo(iso); handleFilterChange('dateTo', iso); }}
                            />
                        </>
                    )}
                </div>

                {/* Clear filters — visible only when at least one filter is active */}
                {hasActiveFilter && (
                    <button
                        type="button"
                        className={s.clearFiltersLink}
                        onClick={handleClearFilters}
                    >
                        {t('list.clearFilters')}
                    </button>
                )}
            </div>

            {/* CR-2026-05-30 — Search + page-size selector row */}
            <div className={s.toolsRow}>
                <input
                    type="search"
                    aria-label={t('list.search.label')}
                    placeholder={t('list.search.placeholder')}
                    className={s.searchInput}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                />
                <div className={s.pageSizeWrap}>
                    <label htmlFor="page-size" className={s.pageSizeLabel}>{t('list.pageSize')}</label>
                    <select
                        id="page-size"
                        className={s.pageSizeSelect}
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            {/* List body */}
            {isLoading && (
                <div className={s.skeletonList} aria-busy="true">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className={s.skeletonRow} aria-hidden="true" />
                    ))}
                </div>
            )}

            {isError && !isLoading && (
                <div className={s.emptyState} role="alert">
                    <p className={s.emptyMsg}>{t('list.loadError')}</p>
                    <button type="button" className={s.retryBtn} onClick={() => refetch()}>
                        {t('list.retry')}
                    </button>
                </div>
            )}

            {!isLoading && !isError && items.length === 0 && (
                <div className={s.emptyState}>
                    <p className={s.emptyMsg}>{t('list.noResults')}</p>
                    <p className={s.emptyHint}>{t('list.noResultsCta')}</p>
                    <button
                        type="button"
                        className={s.retryBtn}
                        onClick={handleClearFilters}
                    >
                        {t('list.clearFilters')}
                    </button>
                </div>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <div className={s.listBody}>
                    {items.map(appointment => {
                        const canMark = appointment.canMarkPaid;
                        const paymentVariant = appointment.paymentStatus === 'Paid'
                            ? 'paid'
                            : appointment.paymentStatus === 'Refunded'
                                ? 'refunded'
                                : 'unpaid';

                        // Source pill: "App" or "Walk-in" — DIVERGENT from Calendar 📱 emoji (PO Q5 lock)
                        const sourceLabel = appointment.sourceType === 'marketplace'
                            ? t('list.source.app')
                            : t('list.source.walkin');

                        return (
                            <div
                                key={appointment.id}
                                className={s.row}
                                // O1 review (M1) — only the row itself opens the detail:
                                // clicks / Enter on inner controls (Confirm, Decline, Mark paid,
                                // payment badge) never also open it.
                                onClick={e => { if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('button')) handleRowClick(appointment as SalonOwnerAppointmentRowDto); }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) handleRowClick(appointment as SalonOwnerAppointmentRowDto); }}
                                aria-label={`${appointment.serviceName} — ${appointment.clientDisplayName} — ${t(`list.payment.${paymentVariant}`)}`}
                            >
                                {/* Time column */}
                                <div className={s.colTime}>
                                    {/* O1 (AB#2199, SO-05) — every row carries its date, so an old
                                        request can never pass for today's. */}
                                    <span className={s.rowDate} data-testid="row-date">
                                        {formatBookingDate(appointment.appointmentDate, appointment.branchLocalStartTime, language)}
                                    </span>
                                    <span className={s.time} dir="ltr">{appointment.branchLocalStartTime}</span>
                                    <span className={s.duration}>{tl('appt.durationMinutes').replace('{n}', String(appointment.durationMinutes))}</span>
                                </div>

                                {/* Customer column */}
                                <div className={s.colCustomer}>
                                    <span className={s.customerName}>{appointment.clientDisplayName}</span>
                                    {appointment.clientPhoneMasked && (
                                        <span className={s.phone} dir="ltr">{appointment.clientPhoneMasked}</span>
                                    )}
                                    {/* CR-2026-05-30-v3 — Surface the branch so the founder doesn't
                                        wonder "where in Calendar does this live?". Always shown to
                                        owners; staff already see their own branch via the scope pill. */}
                                    <div className={s.metaRow}>
                                        <span className={s.sourcePill}>{sourceLabel}</span>
                                        {!isStaff && (
                                            <span className={s.branchPill} title={appointment.branchName}>
                                                <span className={s.branchPillIcon} aria-hidden="true">📍</span>
                                                {appointment.branchName}
                                                {appointment.branchIsHQ && <span className={s.branchPillHq}>{tl('cal.hq')}</span>}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Service column */}
                                <div className={s.colService}>
                                    <span className={s.serviceName}>
                                        {appointment.serviceName}
                                        {/* AB#2034 — bundle badge: this row is one leg of a
                                            customer package bundle. Title carries the bundle name
                                            (bidi-isolated) when the BE resolved it. */}
                                        {(appointment as SalonOwnerAppointmentRowDto).bundleBookingId != null && (
                                            <span
                                                className={s.bundleBadge}
                                                title={(appointment as SalonOwnerAppointmentRowDto).bundleName
                                                    ? `⁨${(appointment as SalonOwnerAppointmentRowDto).bundleName}⁩`
                                                    : undefined}
                                            >
                                                {t('bundle.tag')}
                                            </span>
                                        )}
                                    </span>
                                    <span className={s.amount} dir="ltr">
                                        {appointment.amount} {appointment.currencyIso}
                                    </span>
                                </div>

                                {/* Staff + Branch column */}
                                <div className={s.colStaff}>
                                    <span className={s.staffInitials}>{appointment.staffInitials}</span>
                                    <div>
                                        <span className={s.staffName}>{appointment.staffDisplayName}</span>
                                        <span className={s.branchName}>
                                            {appointment.branchName}
                                            {appointment.branchIsHQ && <span className={s.hqBadge}>{tl('cal.hq')}</span>}
                                        </span>
                                    </div>
                                </div>

                                {/* Status + Payment pills */}
                                <div className={s.colPills}>
                                    {/* O1 (AB#2199) — the same status words + colours as the
                                        Calendar booking detail (lib/bookingLifecycle). */}
                                    <BookingStatusPill
                                        status={appointment.status}
                                        cancellationSource={(appointment as SalonOwnerAppointmentRowDto).cancellationSource}
                                    />
                                    <PaymentBadge
                                        variant={paymentVariant}
                                        size="md"
                                        onClick={canMark ? () => {
                                            handleOpenMarkPaid(appointment as SalonOwnerAppointmentRowDto);
                                        } : undefined}
                                        ariaLabel={canMark
                                            ? `${appointment.clientDisplayName} ${t('list.payment.unpaid')} — ${tl('booking.action.markPaid')}`
                                            : undefined}
                                    />
                                </div>

                                {/* O1 (AB#2199, SO-04) — a request is answered from its row:
                                    Confirm / Decline (backend-allowed only; Decline asks first).
                                    S1 (AB#2204, SG-04 / SG-13) — staff rows too, from the same
                                    backend allowedTransitions, through the staff endpoint. */}
                                {toLifecycleStatus(appointment.status) === 'Pending' && (
                                    <div className={s.rowActions} data-testid="row-request-actions">
                                        <BookingActions
                                            appointmentId={appointment.id}
                                            status={appointment.status}
                                            allowedTransitions={appointment.allowedTransitions}
                                            only={['Approved', 'Cancelled']}
                                            compact
                                            actor={isStaff ? 'staff' : 'owner'}
                                            onChanged={() => { void refetch(); }}
                                        />
                                    </div>
                                )}

                                {/* Mark Paid action */}
                                {canMark && (
                                    <button
                                        type="button"
                                        className={s.markPaidBtn}
                                        onClick={e => { e.stopPropagation(); handleOpenMarkPaid(appointment as SalonOwnerAppointmentRowDto); }}
                                        aria-label={tl('booking.action.markPaid')}
                                    >
                                        {tl('booking.action.markPaid')}
                                    </button>
                                )}
                            </div>
                        );
                    })}

                    {data?.hasMore && (
                        <div className={s.loadMore}>
                            <button type="button" className={s.retryBtn}>
                                {t('list.loadMore')}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* S1 (AB#2204, SG-05) — staff: the same detail, staff-scoped. S2 — also
                for a booking that isn't in the current tab (deep link). */}
            {isStaff && detailId != null && (
                <StaffBookingFlow
                    appointmentId={detailId}
                    row={items.find(r => r.id === detailId)}
                    onClose={() => setDetailId(null)}
                />
            )}

            {/* O1 (AB#2199, SO-06) — shared booking detail (same as the Calendar). */}
            {!isStaff && detailId != null && (() => {
                const row = (items as SalonOwnerAppointmentRowDto[]).find(r => r.id === detailId);
                if (!row) return null;
                return (
                    <BookingDetailDialog
                        booking={listBookingModel(row)}
                        onClose={() => setDetailId(null)}
                        onMarkPaid={() => { setDetailId(null); handleOpenMarkPaid(row); }}
                        footerAction={(
                            <button
                                type="button"
                                className={s.detailCalendarBtn}
                                onClick={() => openInCalendar(row)}
                            >
                                {tl('booking.action.openCalendar')}
                            </button>
                        )}
                    />
                );
            })()}

            {/* Mark-paid modal (W3) */}
            {modalAppointment && (
                <MarkAsPaidModal
                    appointmentId={modalAppointment.id}
                    customer={{
                        name: modalAppointment.clientDisplayName,
                        phone: (modalAppointment as SalonOwnerAppointmentRowDto).clientPhoneFull,
                    }}
                    service={{
                        name: modalAppointment.serviceName,
                        amount: modalAppointment.amount,
                        currency: modalAppointment.currencyIso,
                    }}
                    staff={{
                        name: modalAppointment.staffDisplayName,
                        branch: modalAppointment.branchName,
                    }}
                    isOpen={!!modalAppointment}
                    onClose={() => setModalAppointment(null)}
                    onSuccess={handleMarkPaidSuccess}
                    idempotencyKey={idempotencyKeyRef.current}
                    source="list"
                    // AB#2034 — pass the bundle context so the detail modal badges a bundle leg.
                    bundle={modalAppointment.bundleBookingId != null
                        ? { name: modalAppointment.bundleName }
                        : undefined}
                />
            )}
        </div>
    );
};

export default AppointmentsListPage;
